<template>
  <span>
    <div
      v-if="!splitted && !showPotree"
      class="fit"
    >
      <iframe
        name="mapiaPano"
        v-if="!showLateral"
        :src="srcPannellum"
        @load="loadPanoramaFrame"
        frameborder="0"
        :class="horizontal && 'fit no-margin'"
        :style="!horizontal && `height: calc(${offset} - 5px); width: 100%`"
      />
      <pinch-zoom v-else>
        <img
          :src="srcLateral"
          :style="horizontal ? `height: calc(${offset} - 20px)` : `height: ${offset}`"
        />
      </pinch-zoom>
    </div>
    <div
      v-else-if="!splitted && !showPannellum"
      class="fit"
    >
      <iframe
        allowfullscreen
        name="mapiaPointCloud"
        :src="srcPotree"
        @load="updatePointCloudFrame"
        class="fit no-margin"
        frameborder="0"
      />
    </div>
    <q-splitter
      v-else
      v-model="splitterModel"
      :horizontal="horizontal"
      :style="horizontal && `height: ${splitterHeight}`"
    >
      <template v-slot:before>
        <iframe
          name="mapiaPano"
          v-if="!showLateral"
          :src="srcPannellum"
          @load="loadPanoramaFrame"
          :style="horizontal ? `height: ${iframeHeightBefore}; width: 100%` : `height: calc(${offset} - 8px); width: 100%`"
          frameborder="0"
        />
        <pinch-zoom
          v-else
          :style="horizontal ? `height: ${iframeHeightBefore}` : `height: ${offset}`"
        >
          <img :src="srcLateral"/>
        </pinch-zoom>
      </template>
      <template v-slot:separator>
        <q-avatar color="primary" text-color="white" size="30px" :icon="horizontal ? 'unfold_more' : 'code'" />
      </template>
      <template v-slot:after>
        <iframe
          allowfullscreen
          name="mapiaPointCloud"
          :src="srcPotree"
          @load="updatePointCloudFrame"
          :style="horizontal ? `height: ${iframeHeightAfter}; width: 100%` : `height: calc(${offset} - 8px); width: 100%`"
          frameborder="0"
        />
      </template>
    </q-splitter>
  </span>
</template>

<script>
import axios from 'axios'
import PinchZoom from 'vue-pinch-zoom'
import { QAvatar, QSplitter } from 'quasar'
import { mapState } from 'vuex'
import proj4 from 'proj4'
import { calculateTargetPoint } from 'src/lib/geometry'
import { projections } from 'src/lib/projections'

export default {
  props: ['horizontal', 'offset'],
  components: {
    PinchZoom,
    QAvatar,
    QSplitter
  },
  beforeMount () {
    this.$store.dispatch('panorama/getCampaigns')
    proj4.defs(projections)
    this.splitter()
  },
  mounted () {
    this.$nextTick(() => {
      this.addEventListener()
    })
  },
  data () {
    return {
      splitterModel: 50 // start at 50%
    }
  },
  computed: {
    ...mapState({
      username: state => state.auth.username,
      accessToken: state => state.auth.accessToken,
      altitude: state => state.panorama.altitude,
      head: state => state.panorama.head,
      headFrom: state => state.panorama.headFrom,
      latLng: state => state.panorama.latLng,
      name: state => state.panorama.name,
      pan: state => state.panorama.pan,
      pitch: state => state.panorama.pitch,
      roll: state => state.panorama.roll,
      metadataMethods: state => state.panorama.metadataMethods,
      campaigns: state => state.panorama.campaigns,
      selectedCampaign: state => state.panorama.selectedCampaign,
      srcPannellum: state => state.panorama.srcPannellum,
      srcLateral: state => state.panorama.srcLateral,
      srcPotree: state => state.panorama.srcPotree,
      showPotree: state => state.panorama.showPotree,
      showLateral: state => state.panorama.showLateral,
      showPannellum: state => state.panorama.showPannellum,
      currentPointclouds: state => state.panorama.currentPointclouds
    }),
    splitted () {
      return this.showPannellum && this.showPotree
    },
    splitterHeight () {
      return `calc(${this.offset} - 20px)`
    },
    iframeHeightBefore () {
      return `calc(${this.offset} * ${this.splitterModel} / 100 - 20px)`
    },
    iframeHeightAfter () {
      return `calc(${this.offset} * ${100 - this.splitterModel} / 100 - 20px)`
    }
  },
  methods: {
    addEventListener: function () {
      const self = this
      // Listen to message from iframe element
      window.addEventListener('message', function (msg) {
        if (msg.data && msg.data.type === 'rotation') {
          const head = 90 - (msg.data.head + self.pan)
          if (!Number.isNaN(head)) {
            self.$store.commit('panorama/setHead', { head, from: 'pannellum' })
          }
        } else if (msg.data && msg.data.type === 'nextpano') {
          const latLng = {
            lat: parseFloat(msg.data.lat),
            lng: parseFloat(msg.data.lng)
          }
          self.$store.commit('panorama/setCampaignSelectedByUser', true)
          self.$store.dispatch('panorama/getPanoByPoint', latLng)
          const head = 90 - (msg.data.asimuth + self.pan)
          if (!Number.isNaN(head)) {
            self.$store.commit('panorama/setHead', { head, from: 'pannellum' })
          }
        } else if (msg.data?.type === 'syncpano') {
          const latLng = {
            lat: parseFloat(msg.data.lat),
            lng: parseFloat(msg.data.lng)
          }
          self.syncPanoToPotree(latLng)
        }
      })
    },
    async syncPanoToPotree (latLng) {
      const poi = await this.__getPoiPCLatLng(latLng)
      const iframe = window.frames['mapiaPointCloud']
      iframe.mapiaPotree.activateImage360(poi)
    },
    async __getPoiPCLatLng (latLng) {
      const url = this.$config.panoramas.search
      const config = {
        params: {
          p: `${latLng.lat},${latLng.lng}`,
          r: this.$config.panoramas.radius,
          sc: this.$store.state.panorama.campaignSelectedByUser?.id || ''
        },
        headers: this.$store.getters['auth/headers']
      }
      const response = await axios.get(url, config)
      const poi = response.data?.poi?.features.length > 0 && response.data.poi.features[0]
      const canSyncCampaigns = this.campaigns.filter(campaign => campaign.sync_pano)
      if (!canSyncCampaigns.map(campaign => campaign.id).includes(poi.properties.campaign)) {
        return null
      }
      if (poi?.properties) {
        poi.properties.folder = this.__getPoiFolder(poi)
      }
      return poi
    },
    __getPoiFolder (poi) {
      if (poi.properties.folder?.startsWith('http')) {
        return poi.properties.folder
      }
      const poiCampaign = this.campaigns.filter(campaign => campaign.id === poi.properties.campaign)[0]
      let folder = [poiCampaign.folder_pano, poi.properties.folder].join('/')
      if (!folder) {
        return this.$config.panoramas.media
      }
      if (!folder.endsWith('/')) {
        folder = folder + '/'
      }
      if (!folder.startsWith('https')) {
        folder = this.$config.panoramas.media + folder
      }
      return folder
    },
    loadPanoramaFrame () {
      this.updatePanoramaNeighbours()
      this.rotatePanoramaFrame()
    },
    updatePanoramaNeighbours () {
      if (!this.srcPannellum || !this.name) {
        return
      }
      const iframe = window.frames['mapiaPano']
      if (iframe && iframe.mapiaPano && iframe.mapiaPano.updateNeighbours) {
        const neighbours = this.$store.state.panorama.neighbours
        const pointNeighbours = neighbours.map(neighbour => {
          const coords = [...neighbour.geometry.coordinates]
          const point = [...coords.reverse(), neighbour.properties.altitude]
          return point.join(',')
        }).join('|')
        iframe.mapiaPano.updateNeighbours(pointNeighbours)
      }
    },
    rotatePanoramaFrame () {
      if (!this.srcPannellum || !this.name) {
        return
      }
      const iframe = window.frames['mapiaPano']
      if (this.headFrom !== 'pannellum' && iframe && iframe.rotatePanorama) {
        const rotation = 90 - (this.head + this.pan)
        iframe.rotatePanorama(rotation)
      }
    },
    updatePointCloudFrame () {
      this.sendPointCloudCampaigns()
      this.movePointCloudFrame()
    },
    movePointCloudFrame () {
      if (!this.srcPotree) {
        return
      }
      const iframe = window.frames['mapiaPointCloud']
      if (this.latLng && this.head && iframe && iframe.mapiaPotree) {
        const epsg = this.selectedCampaign ? this.selectedCampaign.epsg : this.campaigns[0].epsg
        const latLngProj = proj4('EPSG:4326', `EPSG:${epsg}`).forward([this.latLng.lng, this.latLng.lat])
        const latLngTarget = calculateTargetPoint(latLngProj, this.head, 100)
        const positionPointCloud = [latLngProj[0], latLngProj[1], this.altitude]
        const targetPointCloud = [latLngTarget[0], latLngTarget[1], this.altitude]
        iframe.mapiaPotree.moveTo(positionPointCloud, targetPointCloud)
        const projection = projections.find(proj => proj[0] === `EPSG:${epsg}`)[1]
        iframe.mapiaPotree.setOption('projection', projection)
      }
    },
    sendPointCloudCampaigns () {
      const campaigns = this.campaigns.map(campaign => {
        let title = campaign.date_start.split('-').reverse().join('/') + ' - ' + campaign.name
        if (campaign.metadata) {
          title += ' ('
          let metadata = []
          if (campaign.metadata.method) {
            metadata.push(this.metadataMethods[campaign.metadata.method - 1])
          }
          if (campaign.metadata.precision) {
            metadata.push(campaign.metadata.precision)
          }
          title += metadata.join(' - ') + ')'
        }
        campaign['title'] = title
        campaign['pointclouds'] = this.currentPointclouds.map(pc => {
          delete pc.geometry
          return pc
        }).filter(pc => pc.properties.campaign === campaign.id)
        delete campaign.geom
        return campaign
      }).filter(campaign => campaign.pointclouds.length > 0)
      const iframe = window.frames['mapiaPointCloud']
      if (iframe && iframe.mapiaPotree) {
        iframe.mapiaPotree.loadLayers(campaigns)
        const defaultPcs = this.currentPointclouds.filter(pc => pc.properties.campaign === this.currentPointclouds[0].properties.campaign)
        iframe.mapiaPotree.loadDefaultPointclouds(defaultPcs)
      }
    },
    splitter () {
      const config = {
        headers: this.$store.getters['auth/headers']
      }
      axios.get(this.$config.panoramas.zone, config)
        .then(response => {
          const hasPcPermission = response.data.features.filter(zone => zone.properties.pc_permission).length > 0
          this.$store.commit('panorama/setShowPotree', hasPcPermission)
          const hasPoiPermission = response.data.features.filter(zone => zone.properties.poi_permission).length > 0
          this.$store.commit('panorama/setShowPannellum', hasPoiPermission)
        })
    }
  },
  watch: {
    latLng: ['updatePointCloudFrame'],
    head: ['rotatePanoramaFrame', 'movePointCloudFrame'],
    srcPannellum: function () {
      this.rotatePanoramaFrame()
    },
    srcPotree: function () {
      this.updatePointCloudFrame()
    },
    accessToken (val) {
      this.$store.dispatch('panorama/getCampaigns')
      this.splitter()
    }
  }
}
</script>
